import queryString from 'query-string';

import { FrontEndCarrierRead } from '@headway/api/models/FrontEndCarrierRead';
import { FrontEndCarrierReadByUser } from '@headway/api/models/FrontEndCarrierReadByUser';
import { ProviderSearchFilters } from '@headway/api/models/ProviderSearchFilters';
import { UnitedStates } from '@headway/api/models/UnitedStates';
import statesToDisplayNames from '@headway/shared/constants/unitedStatesDisplayNames';
import {
  isBcbsCarrierStateInsuranceCarrierComboOutsideOfHomeState,
  isCarrierInHeadwayNetworkForState,
} from '@headway/shared/utils/carriers';
import {
  canCarrierBeAcceptedInState,
  isFrontEndCarrierIdBcbs,
} from '@headway/shared/utils/insuranceUtils';
import { filtersToQueryParams } from '@headway/shared/utils/providerSearchFilters';
import { logException } from '@headway/shared/utils/sentry';

import { ZipCodeInputFormSchemaType } from './types';

export const shouldTriggerBlueCardFlow = (
  carrierId: number,
  insuranceCarriers: FrontEndCarrierReadByUser[],
  state?: UnitedStates
): boolean => {
  const frontEndCarrier = insuranceCarriers
    ? insuranceCarriers.find((item) => item.id === carrierId)
    : undefined;
  if (!frontEndCarrier || !state) return false;
  if (
    isCarrierInHeadwayNetworkForState(frontEndCarrier, state) &&
    !isBcbsCarrierStateInsuranceCarrierComboOutsideOfHomeState(
      frontEndCarrier,
      state
    )
  ) {
    return false;
  }

  return isFrontEndCarrierIdBcbs(carrierId);
};

/**
 * Creates search filters with optional BlueCard parameters
 */
export const transformZipCodeInputFormValuesToSearchFilters = (
  values: Partial<ZipCodeInputFormSchemaType>
): ProviderSearchFilters => {
  const {
    state,
    stateSlug,
    insurance,
    frontEndCarrierId,
    isBlueCard,
    ...formValuesWithEquivalentSearchFilterKeys
  } = values;
  let filters = {
    ...formValuesWithEquivalentSearchFilterKeys,
    state: values.state,
    frontEndCarrierId: values.insurance || values.frontEndCarrierId,
  } as ProviderSearchFilters;

  if (values.isBlueCard) {
    filters.initialFrontEndCarrierId = values.insurance;
    filters.isBlueCard = true;
  }

  return filters;
};

export const transformProviderSearchFiltersAndZipCodeToSearchParams = (
  filters: ProviderSearchFilters,
  zipCode: string | undefined,
  stateSlug: string | undefined
) => {
  const { state: _state, ...queryParams } = filtersToQueryParams(filters)[0];
  // intake query params expect a slug. search query params expect a state OR a slug.
  const searchParams = { ...queryParams, state: stateSlug } as Record<
    string,
    any
  >;

  // Add zipCode to query params separately since it's not part of ProviderSearchFilters
  if (zipCode) {
    searchParams.zipCode = zipCode;
  }

  return searchParams;
};

/**
 * Returns the search path from the ZipCodeInputForm values
 */
export const getSearchPathFromFormValues = (
  values: ZipCodeInputFormSchemaType
) => {
  const { zipCode, stateSlug } = values;

  const filters = transformZipCodeInputFormValuesToSearchFilters(values);
  const searchParams = transformProviderSearchFiltersAndZipCodeToSearchParams(
    filters,
    zipCode,
    stateSlug
  );

  return `/search/${stateSlug}?${queryString.stringify(searchParams)}`;
};

/**
 * Returns the location name from the ZipCodeInputForm values
 */
export const getLocationNameFromFormValues = (
  values: ZipCodeInputFormSchemaType
) => {
  // NOTE: state = NEW_YORK, stateSlug = new-york
  const { state } = values;

  if (!state) {
    return undefined;
  }

  try {
    const displayName = statesToDisplayNames[state as UnitedStates];
    return displayName;
  } catch (error) {
    logException(error, {
      extra: {
        message:
          '[getLocationNameFromFormValues] Error getting state name from state',
        state,
      },
    });
    return undefined;
  }
};

export const isCarrierValidForState = ({
  frontEndCarrierId,
  insuranceCarriers,
  state,
}: {
  frontEndCarrierId: number;
  insuranceCarriers: FrontEndCarrierRead[];
  state: UnitedStates;
}) => {
  const selectedCarrier = insuranceCarriers?.find(
    (carrier) => carrier.id === frontEndCarrierId
  );

  return !!(
    selectedCarrier &&
    canCarrierBeAcceptedInState(selectedCarrier, state as UnitedStates)
  );
};
